// src/context/AuthContext2.jsx
import { createContext, useState, useEffect, useContext } from 'react';
import useRequest from 'src/hooks/api/useRequest';
import { getUserToken } from 'src/utils/apiRequests';
import { getFromCommonDB, setCacheUser, setToCommonDB } from 'src/utils/cache/idbUtility';

import {
  initializeTokenManager,
  on as onTokenEvent,
  getAuthToken,
  setTokens as tmSetTokens,
  clearTokens as tmClearTokens,
  hydrateFromDB,
} from 'src/utils/singletonToken';

export const AuthContext2 = createContext(null);

export function AuthProvider2({ children }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [username, setUsername] = useState(null);

  // NOTE: now also pulling out `status`
  const [tokenResult, setRequest, abort, loading, status] = useRequest();

  useEffect(() => {
    // Token manager hydrates from the common store on boot
    initializeTokenManager(null).finally(async () => {
      setIsInitialized(true);
      setIsAuthenticated(!!getAuthToken());

      // restore per-user store pointer (for non-token caches)
      try {
        const last = await getFromCommonDB<string>('LAST_USERNAME');
        if (last) {
          setUsername(last);
          setCacheUser(last); // sets currentStore for reads (non-token)
        }
      } catch {}
    });

    const offUpdated = onTokenEvent('updated', () => {
      setIsAuthenticated(!!getAuthToken());
    });
    const offLoggedOut = onTokenEvent('loggedOut', () => {
      setIsAuthenticated(false);
      setCacheUser(null);
      setUsername(null);
      setToCommonDB('LAST_USERNAME', null).catch(() => {});
    });

    return () => {
      offUpdated();
      offLoggedOut();
    };
  }, []);

  // When login API resolves, push new tokens into manager (token stored in common store)
  useEffect(() => {
    if (!tokenResult) return;
    if (tokenResult?.access_token) {
      const now = Date.now();
      const earlySkewSec = 45; // refresh just a little early
      const expiresInNum =
        typeof tokenResult.expires_in === 'string'
          ? parseInt(tokenResult.expires_in, 10)
          : tokenResult.expires_in ?? 0;

      const effectiveExpMs =
        typeof tokenResult.expMs === 'number'
          ? tokenResult.expMs
          : now + Math.max(0, expiresInNum - earlySkewSec) * 1000;

      tmSetTokens({
        access_token: tokenResult.access_token,
        refresh_token: tokenResult.refresh_token,
        expires_in: expiresInNum,
        expMs: effectiveExpMs,
      });
      setIsAuthenticated(true);
    }
  }, [tokenResult]);

  const login = (usernameInput, password) => {
    abort();
    setUsername(usernameInput);
    setCacheUser(usernameInput); // for per-user caches (NOT tokens)
    setToCommonDB('LAST_USERNAME', usernameInput).catch(() => {});
    hydrateFromDB().catch(() => {}); // harmless; will do nothing if no tokens
    setRequest(getUserToken(usernameInput, password));
  };

  const logout = async () => {
    tmClearTokens(); // clears common-store tokens + broadcasts
    setIsAuthenticated(false);
    setUsername(null);
    setCacheUser(null);
  };

  return (
    <AuthContext2.Provider
      value={{
        isInitialized,
        isAuthenticated,
        loading,
        status,        
        login,
        logout,
        username,
      }}
    >
      {children}
    </AuthContext2.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext2);
}

export default AuthContext2;
